/**
 * Kiyoko Beauty — Routine Discount Manager
 *
 * Silently applies/removes the ROUTINE15 discount code based on whether
 * all recommended routine products are present in the cart.
 *
 * Works across all pages — not just the quiz results page.
 */

(function () {
  "use strict";

  const DISCOUNT_CODE = "ROUTINE10";
  const ROUTINE_KEY = "kiyoko_routine_variants";
  const DEBOUNCE_DELAY = 1000;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let debounceTimer = null;

  /** Prevents re-entrant checks while discount is being applied/removed */
  let isUpdatingDiscount = false;

  // ── Routine storage ───────────────────────────────────────────────────────

  /**
   * Returns the saved routine variant IDs from localStorage.
   * @returns {number[]}
   */
  function getRoutineVariants() {
    try {
      const stored = localStorage.getItem(ROUTINE_KEY);
      if (stored) return JSON.parse(stored);
    } catch (_e) {}
    return [];
  }

  /**
   * Saves routine variant IDs to localStorage.
   * @param {number[]} variants
   */
  function saveRoutineVariants(variants) {
    try {
      localStorage.setItem(ROUTINE_KEY, JSON.stringify(variants));
    } catch (_e) {}
  }

  // ── DOM scraping (results page only) ─────────────────────────────────────

  /**
   * Reads rendered product card variant IDs from the quiz results page DOM
   * and persists them as the current routine.
   */
  function buildRoutineFromDOM() {
    const cards = document.querySelectorAll(".qr-step__right .qr-product-card");
    if (!cards.length) return;

    /** @type {number[]} */
    const variantIds = [];

    cards.forEach((card) => {
      const atcBtn = card.querySelector(".qr-product-card__atc");
      if (!atcBtn || !atcBtn.dataset.variantId) return;

      const vid = parseInt(atcBtn.dataset.variantId, 10);
      if (vid && !variantIds.includes(vid)) {
        variantIds.push(vid);
      }
    });

    if (variantIds.length > 0) {
      saveRoutineVariants(variantIds);
    }
  }

  // ── Cart helpers ──────────────────────────────────────────────────────────

  /**
   * Fetches the current cart state.
   * @returns {Promise<Object>}
   */
  async function getCart() {
    const response = await fetch("/cart.js", {
      headers: { "Content-Type": "application/json" },
    });
    return response.json();
  }

  /**
   * Returns the currently applied discount codes from the cart.
   * @param {Object} cart
   * @returns {string[]}
   */
  function getExistingDiscounts(cart) {
    return (cart.discount_codes || []).map(
      (/** @type {{ code: string }} */ d) => d.code,
    );
  }

  /**
   * Applies a discount code to the cart without removing existing codes.
   * @param {string} code
   */
  /** @returns {string} The cart update AJAX URL */
  function cartUpdateUrl() {
    return "/cart/update.js";
  }

  async function applyDiscount(code) {
    const cart = await getCart();
    const existing = getExistingDiscounts(cart);
    if (existing.includes(code)) return;

    await fetch(cartUpdateUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ discount: [...existing, code].join(",") }),
    });
    // Do NOT dispatch cart:refresh here — would cause re-entrant loop
  }

  /**
   * Removes a discount code from the cart, preserving any others.
   * @param {string} code
   */
  async function removeDiscount(code) {
    const cart = await getCart();
    const existing = getExistingDiscounts(cart);
    if (!existing.includes(code)) return;

    const remaining = existing.filter((c) => c !== code).join(",");

    await fetch(cartUpdateUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ discount: remaining }),
    });
    // Do NOT dispatch cart:refresh here — would cause re-entrant loop
  }

  // ── Core check ────────────────────────────────────────────────────────────

  /**
   * Compares the current cart against the saved routine.
   * Applies ROUTINE15 if all routine items are present, removes it if not.
   */
  async function checkRoutineDiscount() {
    if (isUpdatingDiscount) return;

    const routineVariants = getRoutineVariants();
    if (!routineVariants.length) return;

    isUpdatingDiscount = true;

    try {
      const cart = await getCart();
      const cartVariantIds = cart.items.map(
        (/** @type {{ variant_id: number }} */ item) => item.variant_id,
      );
      const allPresent = routineVariants.every((vid) =>
        cartVariantIds.includes(vid),
      );

      if (allPresent) {
        await applyDiscount(DISCOUNT_CODE);
      } else {
        await removeDiscount(DISCOUNT_CODE);
      }
    } catch (error) {
      console.warn("[RoutineDiscount] Cart check failed:", error);
    } finally {
      // Release guard quickly so subsequent cart changes aren't blocked
      setTimeout(() => {
        isUpdatingDiscount = false;
      }, 500);
    }
  }

  /** Debounced wrapper around checkRoutineDiscount */
  function debouncedCheck() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(checkRoutineDiscount, DEBOUNCE_DELAY);
  }

  // ── Cart change detection ─────────────────────────────────────────────────

  /**
   * Intercepts fetch — applied late so it wraps any theme overrides.
   * Falls back to XHR patching and event listening if overridden.
   */
  function interceptFetch() {
    // Delay wrapping so we run after global.js and other theme scripts
    setTimeout(() => {
      const originalFetch = window.fetch;

      window.fetch = async function (...args) {
        const result = await originalFetch.apply(this, args);
        const url =
          typeof args[0] === "string" ? args[0] : (args[0]?.url ?? "");

        const isCartItemChange =
          !isUpdatingDiscount &&
          (url.startsWith("/cart/") ||
            url.startsWith(window.location.origin + "/cart/")) &&
          (url.includes("/cart/add") ||
            url.includes("/cart/change") ||
            url.includes("/cart/clear"));

        if (isCartItemChange) {
          debouncedCheck();
        }

        return result;
      };
    }, 2000); // Wait 2s for all scripts to load
  }

  /** Binds theme-level cart events as additional triggers */
  function bindCartEvents() {
    // All possible cart event names across theme components
    const cartEvents = [
      "cart:refresh",
      "cart:updated",
      "cart:update", // ThemeEvents.cartUpdate from component-cart-items.js
      "cart:add", // ThemeEvents.cartAdd
      "cart:change",
      "rebuy:cart.change",
      "shopify:section:load",
    ];

    cartEvents.forEach((evt) => {
      document.addEventListener(evt, () => {
        if (!isUpdatingDiscount) debouncedCheck();
      });
    });

    // Also watch the cart item count element for DOM changes
    // This catches cases where events don't bubble to document
    const countObserver = new MutationObserver(() => {
      if (!isUpdatingDiscount) debouncedCheck();
    });

    // Observe cart count badges/bubbles
    document
      .querySelectorAll(
        "[js-cart-count], .cart-count, .cart-bubble, [data-cart-count]",
      )
      .forEach((el) => {
        countObserver.observe(el, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      });

    // Also observe body for dynamically added cart count elements
    const bodyObserver = new MutationObserver(() => {
      const countEls = document.querySelectorAll(
        "[js-cart-count], .cart-count, .cart-bubble, [data-cart-count]",
      );
      countEls.forEach((el) => {
        if (!el.dataset.rdObserved) {
          el.dataset.rdObserved = "1";
          countObserver.observe(el, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      });
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });
  }

  // ── Results page observer ─────────────────────────────────────────────────

  /**
   * On the quiz results page, watches for product cards to finish rendering
   * then builds and saves the routine variant list.
   */
  function watchResultsPage() {
    if (!document.getElementById("qr-container")) return;

    // Poll every 500ms until all 6 steps have loaded, then save routine
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      const btns = document.querySelectorAll(
        ".qr-product-card__atc[data-variant-id]",
      );
      // All 6 steps loaded or max attempts reached
      if (btns.length >= 6 || attempts >= 20) {
        clearInterval(poll);
        if (btns.length > 0) {
          buildRoutineFromDOM();
          checkRoutineDiscount();
        }
      }
    }, 500);

    // Also hook into Add All button — save routine right before cart add
    document.addEventListener(
      "click",
      (e) => {
        const btn = e.target.closest("#qr-add-all-top, #qr-add-all-sticky");
        if (btn) {
          buildRoutineFromDOM();
        }
      },
      true,
    ); // capture phase so it runs before the click handler
  }

  // ── Init ──────────────────────────────────────────────────────────────────

  function init() {
    interceptFetch();
    bindCartEvents();
    watchResultsPage();

    // Run on every page load in case cart was modified in another tab
    checkRoutineDiscount();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
